import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Calculator, Bank, Factory, UsersThree, EnvelopeSimple, Database, EnvelopeOpen, Globe, BellRinging, MegaphoneSimple, HardDrives, CursorClick, TrendUp, TrendDown, ArrowsClockwise, CurrencyEur, Headset, Storefront } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const AREAS = [
  { key: "contabilita", name: "Contabilità", color: "#38BDF8", icon: Calculator },
  { key: "paghe", name: "Paghe", color: "#6366F1", icon: CurrencyEur },
  { key: "tesoreria", name: "Tesoreria", color: "#E9A23B", icon: Bank },
  { key: "sohelp", name: "So.Help", color: "#10B981", icon: Headset },
  { key: "posta", name: "Posta", color: "#F97316", icon: EnvelopeSimple },
  { key: "risorice", name: "Risorice", color: "#E05E3F", icon: UsersThree },
  { key: "produzione", name: "Produzione", color: "#9F1239", icon: Factory },
  { key: "paqway", name: "PAQ.WAY", color: "#F43F5E", icon: Storefront },
];

// anello attorno al hub (viewBox 700x500), calcolato in base al numero di app
const POS = Object.fromEntries(AREAS.map((a, i) => {
  const ang = -Math.PI / 2 + (i * 2 * Math.PI) / AREAS.length;
  return [a.key, { x: Math.round(350 + 195 * Math.cos(ang)), y: Math.round(245 + 185 * Math.sin(ang)) }];
}));
const HUB = { x: 350, y: 245 };

const STATUS_COLOR = { ok: "#34D399", warn: "#FBBF24", down: "#EF4444", unknown: "#71717A" };
const STATUS_LABEL = { ok: "OK", warn: "Attenzione", down: "Non raggiungibile", unknown: "Sconosciuto" };
const CHECK_LABEL = {
  ok: { text: "OK", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  warn: { text: "Attenzione", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  down: { text: "Non raggiungibile", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  missing: { text: "Da implementare", cls: "bg-white/5 text-white/45 border-white/15" },
  pending: { text: "In attesa", cls: "bg-white/5 text-white/45 border-white/15" },
  unknown: { text: "Sconosciuto", cls: "bg-white/5 text-white/45 border-white/15" },
};
const Badge = ({ status, testId }) => {
  const m = CHECK_LABEL[status] || CHECK_LABEL.unknown;
  return <span data-testid={testId} className={`inline-flex items-center text-[10px] font-semibold border rounded-full px-2 py-0.5 ${m.cls}`}>{m.text}</span>;
};

const PORTAL_CHECKS = [
  { key: "database", label: "Database", icon: Database },
  { key: "smtp", label: "SMTP servizio", icon: EnvelopeOpen },
  { key: "gsc", label: "Search Console", icon: Globe },
  { key: "push", label: "Notifiche push", icon: BellRinging },
  { key: "ads", label: "Google Ads", icon: MegaphoneSimple },
];

export default function WebAppPage() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState([]);
  const [seoClicks, setSeoClicks] = useState(null);

  const loadHistory = () => api.get("/admin/webapp/doctor/history").then(({ data: h }) => setHistory(h)).catch(() => {});

  const runDoctor = async () => {
    setRunning(true);
    try {
      const { data: d } = await api.post("/admin/webapp/doctor");
      setData(d);
      loadHistory();
      const down = d.results.filter((r) => r.overall === "down").length;
      toast[down ? "warning" : "success"](down ? `Visita completata: ${down} webapp non raggiungibili` : "Visita completata: nessun problema critico");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get("/admin/webapp/doctor"),
        api.get("/admin/web/month-clicks"),
      ]);
      setData(d);
      setSeoClicks(c);
      loadHistory();
      toast.success("Dati aggiornati");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    api.get("/admin/webapp/doctor").then(({ data: d }) => setData(d)).catch(() => {});
    api.get("/admin/web/month-clicks").then(({ data: c }) => setSeoClicks(c)).catch(() => {});
    loadHistory();
    if (searchParams.get("run") === "1") runDoctor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const results = data?.results || [];
  const byArea = Object.fromEntries(results.map((r) => [r.area, r]));
  const statusOf = (key) => byArea[key]?.overall || "unknown";
  const portal = data?.portal;
  const portalOk = portal && portal.database === "ok";

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="webapp-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Monitoraggio collegamenti</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">App</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Stato di salute delle webapp e dei loro collegamenti con AREAPAQ (sito, API, SSO, provisioning, dati). La visita viene eseguita automaticamente ogni mattina: se una webapp risulta malata ricevi un avviso email + push.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="webapp-refresh-button" onClick={handleRefresh} disabled={refreshing}
            variant="ghost" className="h-11 px-6 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full font-semibold text-sm">
            <ArrowsClockwise size={16} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Aggiornamento..." : "Aggiorna ora"}
          </Button>
          <Button data-testid="doctor-button" onClick={runDoctor} disabled={running}
            className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
            <Stethoscope size={17} className={`mr-2 ${running ? "animate-pulse" : ""}`} />
            {running ? "Visita in corso..." : "Doctor"}
          </Button>
        </div>
      </div>

      {seoClicks && (
        <div className="mb-8 p-4 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="seo-clicks-widget">
          <div className="flex items-center gap-2">
            <CursorClick size={16} weight="duotone" className="text-emerald-400" />
            <p className="text-xs font-semibold text-white/80">
              Clic Google (SEO) — {new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
            </p>
          </div>
          <div className="flex items-end gap-3 mt-2">
            <p className="font-heading text-3xl font-bold tracking-tighter leading-none" data-testid="seo-clicks-value">
              {seoClicks.clicks.toLocaleString("it-IT")}
            </p>
            {seoClicks.prev_month_clicks > 0 && (
              <p className={`text-xs font-semibold inline-flex items-center gap-1 mb-0.5 ${seoClicks.clicks >= seoClicks.prev_month_clicks ? "text-emerald-400" : "text-red-400"}`} data-testid="seo-clicks-trend">
                {seoClicks.clicks >= seoClicks.prev_month_clicks ? <TrendUp size={13} weight="bold" /> : <TrendDown size={13} weight="bold" />}
                {seoClicks.clicks - seoClicks.prev_month_clicks >= 0 ? "+" : ""}{seoClicks.clicks - seoClicks.prev_month_clicks}
                <span className="text-white/35 font-normal">vs mese scorso ({seoClicks.prev_month_clicks})</span>
              </p>
            )}
          </div>
          <p className="text-[10px] text-white/35 mt-1.5">Totale su tutti i siti monitorati, dal 1° del mese a oggi.</p>
        </div>
      )}

      <div className="p-4 sm:p-6 rounded-lg bg-surface-deep border border-surface-border animate-fade-up" data-testid="doctor-diagram">
        <div className="relative w-full max-w-3xl mx-auto" style={{ aspectRatio: "7 / 5" }}>
          <svg viewBox="0 0 700 500" className="absolute inset-0 w-full h-full">
            {AREAS.map((a) => {
              const p = POS[a.key];
              const st = statusOf(a.key);
              const color = STATUS_COLOR[st];
              const path = `M ${HUB.x} ${HUB.y} L ${p.x} ${p.y}`;
              return (
                <g key={a.key}>
                  <line x1={HUB.x} y1={HUB.y} x2={p.x} y2={p.y}
                    stroke={st === "unknown" ? "rgba(255,255,255,0.15)" : color}
                    strokeOpacity={st === "unknown" ? 1 : 0.35} strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" />
                  {st !== "unknown" && st !== "down" && (
                    <>
                      <circle r="4" fill={color}>
                        <animateMotion dur="2.4s" repeatCount="indefinite" path={path} />
                      </circle>
                      <circle r="2.5" fill={color} opacity="0.6">
                        <animateMotion dur="2.4s" begin="1.2s" repeatCount="indefinite" path={path} />
                      </circle>
                    </>
                  )}
                  {st === "down" && (
                    <circle cx={(HUB.x + p.x) / 2} cy={(HUB.y + p.y) / 2} r="6" fill="none" stroke="#EF4444" strokeWidth="1.5">
                      <animate attributeName="opacity" values="1;0.2;1" dur="1.2s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Hub AREAPAQ */}
          <div className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: `${(HUB.x / 700) * 100}%`, top: `${(HUB.y / 500) * 100}%` }} data-testid="doctor-hub">
            <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-surface border-2 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.06)] ${portal ? (portalOk ? "border-emerald-400/50" : "border-red-500/60") : "border-white/20"}`}>
              <HardDrives size={26} weight="duotone" className="text-white" />
              <span className="font-heading font-bold text-[11px] tracking-tighter mt-1">AREAPAQ</span>
            </div>
          </div>

          {/* Nodi webapp */}
          {AREAS.map((a) => {
            const p = POS[a.key];
            const st = statusOf(a.key);
            const Icon = a.icon;
            const r = byArea[a.key];
            return (
              <div key={a.key} data-testid={`doctor-node-${a.key}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 text-center"
                style={{ left: `${(p.x / 700) * 100}%`, top: `${(p.y / 500) * 100}%` }}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-surface border flex items-center justify-center mx-auto"
                  style={{ borderColor: st === "unknown" ? "rgba(255,255,255,0.15)" : STATUS_COLOR[st] }}>
                  <Icon size={26} weight="duotone" style={{ color: a.color }} />
                </div>
                <p className="text-[11px] sm:text-xs font-semibold mt-1.5 text-white/85">{a.name}</p>
                <p className="text-[9px] sm:text-[10px] font-semibold inline-flex items-center gap-1" style={{ color: STATUS_COLOR[st] }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[st] }} />
                  {STATUS_LABEL[st]}{r?.latency_ms != null && st !== "down" ? ` · ${r.latency_ms}ms` : ""}
                </p>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-white/35 text-center mt-2">
          {data?.checked_at
            ? `Ultima visita: ${new Date(data.checked_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}`
            : "Nessuna visita ancora eseguita: premi \"Doctor\" per controllare lo stato di salute."}
        </p>
      </div>

      {results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {AREAS.map((a) => {
            const r = byArea[a.key];
            if (!r) return null;
            return (
              <div key={a.key} data-testid={`doctor-detail-${a.key}`}
                className="p-5 rounded-lg bg-surface border border-surface-border border-l-4 animate-fade-up"
                style={{ borderLeftColor: STATUS_COLOR[r.overall] }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold tracking-tight" style={{ color: a.color }}>{a.name}</h3>
                  <Badge status={r.overall} testId={`doctor-overall-${a.key}`} />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  {[["Sito raggiungibile", r.site], ["API attive", r.api], ["Auto-login SSO", r.sso],
                    ["Sync utenti", r.provision], ["Dati Analisi", r.metrics], ["Dati Marketing", r.marketing],
                    ["Collegamento VIKI", r.viki || "unknown"]].map(([label, st]) => (
                    <div key={label} className="flex items-center justify-between gap-2">
                      <span className="text-white/50">{label}</span>
                      <Badge status={st} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {portal && (
            <div className="p-5 rounded-lg bg-surface border border-surface-border border-l-4 animate-fade-up"
              style={{ borderLeftColor: portalOk ? STATUS_COLOR.ok : STATUS_COLOR.down }} data-testid="doctor-portal-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold tracking-tight text-white">Portale AREAPAQ</h3>
                <Badge status={portalOk ? "ok" : "down"} testId="doctor-portal-overall" />
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {PORTAL_CHECKS.map((c) => {
                  const Icon = c.icon;
                  const st = portal[c.key] === "missing" ? "pending" : portal[c.key];
                  return (
                    <div key={c.key} className="flex items-center justify-between gap-2">
                      <span className="text-white/50 inline-flex items-center gap-1.5"><Icon size={13} className="text-white/35" /> {c.label}</span>
                      <Badge status={st} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8 animate-fade-up" data-testid="doctor-history">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-1.5">
            <Stethoscope size={13} className="text-violet-400" /> Storico visite
          </p>
          <div className="border border-surface-border rounded-lg bg-surface overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-white/30 border-b border-white/5">
              <span className="w-32">Data e ora</span>
              {AREAS.map((a) => <span key={a.key} className="w-16 text-center truncate hidden sm:block">{a.name.slice(0, 8)}</span>)}
              <span className="ml-auto">Esito</span>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-white/5">
              {history.map((h, i) => {
                const sick = AREAS.filter((a) => (h.summary?.[a.key]?.overall || "unknown") !== "ok").length;
                return (
                  <div key={h.checked_at} className="flex items-center gap-2 px-4 py-2.5 text-xs" data-testid={`doctor-history-row-${i}`}>
                    <span className="w-32 text-white/60">{new Date(h.checked_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                    {AREAS.map((a) => {
                      const s = h.summary?.[a.key];
                      const st = s?.overall || "unknown";
                      return (
                        <span key={a.key} className="w-16 justify-center hidden sm:flex" title={`${a.name}: ${STATUS_LABEL[st]}${s?.latency_ms != null ? ` · ${s.latency_ms}ms` : ""}`}>
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR[st] }} />
                        </span>
                      );
                    })}
                    <span className={`ml-auto font-semibold ${sick === 0 ? "text-emerald-400" : "text-amber-300"}`}>
                      {sick === 0 ? "Tutto OK" : `${sick} con problemi`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-[10px] text-white/35 mt-2">Ogni riga è una visita (manuale o automatica del mattino): i pallini mostrano lo stato di ciascuna webapp in quel momento.</p>
        </div>
      )}

      <p className="text-[11px] text-white/35 mt-8">
        "Da implementare" / "In attesa" indicano funzioni che la webapp non espone ancora (auto-login SSO, endpoint dati): incolla le istruzioni già disponibili nella chat della webapp per attivarle. "Attenzione" = raggiungibile ma con collegamenti incompleti. "Non raggiungibile" = il sito non risponde.
      </p>
    </main>
  );
}
